import React from 'react';
import { View, ScrollView, Platform, StyleSheet, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { NotificationItem } from '@/hooks/common/useNotifications';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { GlassCard } from '@/components/residents/redesign/GlassCard';
import { StarRating } from '@/components/residents/marketplace';
import { formatDateMMMdd } from '@/utils/residents/date';

type Props = {
  selected: NotificationItem | null;
};

export const NotificationDetails: React.FC<Props> = ({ selected }) => {
  const { colors } = useThemeColors();

  if (!selected) {
    return (
      <View style={styles.placeholder}>
        <Text style={[styles.placeholderTitle, { color: colors.text.primary }]}>{strings.notifications.desktop.placeholder.title}</Text>
        <Text style={[styles.placeholderSub, { color: colors.text.subtle }]}>{strings.notifications.desktop.placeholder.subtitle}</Text>
      </View>
    );
  }

  const isReceivedFeedback = selected.notificationType === 'marketplace_notification';

  if (isReceivedFeedback && selected.marketplaceNotification) {
    const metadata = selected.marketplaceNotification.metadata;
    const listingImageUrl = metadata?.listingImageUrl;
    const listingTitle = metadata?.listingTitle || '';
    const rating = metadata?.rating || 0;
    const reviewText = metadata?.reviewText || '';
    const buyerName = metadata?.buyerName || 'Unknown';
    const createdAt = selected.createdAt;

    return (
      <ScrollView contentContainerStyle={styles.detailContent} showsVerticalScrollIndicator={false}>
        <Text style={[styles.detailTitle, { color: colors.text.primary }]}>{selected.title || 'Received feedback'}</Text>

        <GlassCard borderRadius={16}>
          <View style={styles.feedbackRow}>
            <View style={[styles.feedbackAvatar, { backgroundColor: colors.glass.buttonBg }]}>
              {listingImageUrl ? (
                <Image
                  source={{ uri: listingImageUrl }}
                  style={styles.feedbackAvatarImage}
                  resizeMode="cover"
                />
              ) : (
                <View style={[styles.feedbackAvatarPlaceholder, { backgroundColor: colors.glass.buttonBg }]} />
              )}
            </View>

            <View style={styles.feedbackContent}>
              <View style={styles.ratingDateRow}>
                <StarRating rating={rating} size={16} />
                <Text style={[styles.feedbackDate, { color: colors.text.muted }]}>{formatDateMMMdd(createdAt)}</Text>
              </View>

              <Text style={[styles.feedbackListingTitle, { color: colors.text.primary }]} numberOfLines={2}>
                {listingTitle}
              </Text>

              {reviewText ? (
                <Text style={[styles.feedbackReviewText, { color: colors.text.muted }]}>
                  {reviewText}
                </Text>
              ) : null}

              <Text style={[styles.feedbackBuyerName, { color: colors.text.muted }]}>
                {buyerName}
              </Text>
            </View>
          </View>
        </GlassCard>
      </ScrollView>
    );
  }

  const imageFiles = selected.files?.filter(file => {
    const url = file.fileUrl.toLowerCase();
    return url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') ||
           url.includes('.gif') || url.includes('.webp') || url.includes('.heic');
  }) || [];

  return (
    <ScrollView contentContainerStyle={styles.detailContent} showsVerticalScrollIndicator={false}>
      <Text style={[styles.detailTitle, { color: colors.text.primary }]}>{selected.title || selected.responseDescription || strings.notifications.desktop.defaultTitle}</Text>
      {!!selected.description && <Text style={[styles.detailBody, { color: colors.text.secondary }]}>{selected.description}</Text>}
      {!!selected.descriptionHtml && (
        Platform.OS === 'web' ? (
          // eslint-disable-next-line react/no-danger
          (React.createElement('div', {
            style: { color: colors.text.primary, fontSize: 14 },
            dangerouslySetInnerHTML: { __html: selected.descriptionHtml },
          }))
        ) : (
          <Text style={[styles.detailBody, { color: colors.text.secondary }]}>{selected.descriptionHtml}</Text>
        )
      )}

      {imageFiles.length > 0 && (
        <View style={styles.imagesContainer}>
          {imageFiles.map((file) => (
            <Image
              key={file.id}
              source={{ uri: file.fileUrl }}
              style={[styles.attachedImage, { backgroundColor: colors.card.bg }]}
              resizeMode="cover"
            />
          ))}
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  placeholderTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  placeholderSub: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  detailContent: {
    padding: 24,
    gap: 12,
  },
  detailTitle: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
    fontWeight: '800',
  },
  detailBody: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  imagesContainer: {
    marginTop: 16,
    gap: 12,
  },
  attachedImage: {
    width: '100%',
    height: 200,
    borderRadius: 12,
  },

  feedbackRow: {
    flexDirection: 'row',
    gap: 16,
  },
  feedbackAvatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    overflow: 'hidden',
  },
  feedbackAvatarImage: {
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  feedbackAvatarPlaceholder: {
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  feedbackContent: {
    flex: 1,
    gap: 4,
  },
  ratingDateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  feedbackDate: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  feedbackListingTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    lineHeight: 21,
  },
  feedbackReviewText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },
  feedbackBuyerName: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
});

export default NotificationDetails;
